import { useState, useCallback, useRef } from 'react';
import { useApp } from '@context/AppContext';
import { useTelephone } from './useTelephone';
import { getAssignment } from './telephoneRotation';
import GameHUD from '../shared/GameHUD';
import { Check, Send } from 'lucide-react';

/**
 * Broken Telephone — guessing phase.
 * Full-screen overlay (canvas disabled). Shows the previous player's drawing
 * and a text input for the guess.
 */
export default function TelephoneGuessing() {
  const { state } = useApp();
  const { game, user, room } = state;
  const { submitGuess, advanceRound } = useTelephone();
  const [guess, setGuess] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const advancedRef = useRef(false);

  const chainOrder = game.chainOrder || [];
  const currentRound = game.currentRound ?? 0;

  // Get this player's assignment
  const assignment = getAssignment(chainOrder, currentRound, user.id);
  const chain = assignment ? game.chains?.[assignment.chainStarterId] : null;

  // Get the previous step's drawing
  const prevStep = chain?.steps?.[currentRound - 1];
  const drawingImage = prevStep?.imageData || null;
  const drawerName = prevStep?.userId
    ? room.members?.[prevStep.userId]?.nickname || 'Someone'
    : 'Someone';

  // Submit handler
  const handleSubmit = useCallback(async () => {
    if (submitted) return;
    setSubmitted(true);
    await submitGuess(guess);
  }, [submitted, submitGuess, guess]);

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !submitted && guess.trim()) {
      handleSubmit();
    }
  };

  // Host: when timer expires, auto-submit and advance
  const handleTimerExpire = useCallback(async () => {
    if (!submitted) {
      setSubmitted(true);
      await submitGuess(guess);
    }
    if (user.isHost && !advancedRef.current) {
      advancedRef.current = true;
      setTimeout(() => advanceRound(), 1500);
    }
  }, [submitted, submitGuess, guess, user.isHost, advanceRound]);

  // Submission tracking
  const submissions = game.submissions || {};
  const members = Object.entries(room.members || {});
  const submittedCount = Object.keys(submissions).length;

  return (
    <>
      {/* Full-screen overlay — blocks canvas interaction */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
          {/* Header with timer */}
          <div className="px-5 py-4 border-b border-slate-100">
            <GameHUD
              timerEndsAt={game.timerEndsAt}
              timerDuration={game.timerDuration}
              onTimerExpire={handleTimerExpire}
              emoji="🤔"
              label={`Round ${currentRound + 1} of ${game.totalRounds || chainOrder.length}`}
              sublabel="What do you think this is?"
            />
          </div>

          {/* Drawing to guess */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
              <p className="text-xs text-slate-400 font-display mb-2">
                {drawerName} drew this:
              </p>
              {drawingImage ? (
                <img
                  src={drawingImage}
                  alt="Previous player's drawing"
                  className="w-full rounded-lg border border-slate-200 bg-canvas"
                />
              ) : (
                <div className="w-full aspect-video bg-white rounded-lg border border-slate-200 flex items-center justify-center">
                  <p className="text-sm text-slate-300 font-display">No drawing available</p>
                </div>
              )}
            </div>

            {/* Guess input */}
            {!submitted ? (
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-charcoal font-display">
                  What do you think this is?
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={guess}
                    onChange={(e) => setGuess(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your guess..."
                    maxLength={100}
                    autoFocus
                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm font-display text-charcoal placeholder:text-slate-300 focus:outline-none focus:border-amber/40 focus:ring-2 focus:ring-amber/10"
                  />
                  <button
                    onClick={handleSubmit}
                    disabled={!guess.trim()}
                    className="px-5 py-3 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Submit
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-200">
                  <Check className="w-4 h-4 text-green-500" />
                  <p className="text-sm text-green-700 font-display font-semibold">
                    Submitted: &ldquo;{guess || '(no guess)'}&rdquo;
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer: submission tracker */}
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-xs text-slate-400 font-display">
              {submittedCount} of {members.length} submitted
            </p>
            <div className="flex items-center gap-2">
              {members.map(([id, member]) => (
                <div
                  key={id}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    submissions[id]
                      ? 'border-green-400 bg-green-50'
                      : 'border-slate-200 bg-white'
                  }`}
                  title={member.nickname}
                >
                  {submissions[id] ? (
                    <Check className="w-3 h-3 text-green-500" />
                  ) : (
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: member.colour }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
